import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Modal } from 'react-bootstrap';
import translations from '../../translation'; // Import translations file

const PendingTable = ({ language }) => {
  const translate = (key) => translations[language]?.[key] || key;

  const [modalShow, setModalShow] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingData, setPendingData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const rowsPerPage = 10;

  useEffect(() => {
    fetchPendingData();
  }, []);

  const fetchPendingData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_CHECK_USER_API}/api/admin/pending`, {
        method: 'GET',
      });

      const data = await response.json();
      if (data.success && data.data?.length) {
        setPendingData(data.data);
      } else {
        setPendingData([]);
        console.warn(data.message || translate('noPendingData'));
      }
    } catch (error) {
      console.error(translate('fetchError'), error);
      setPendingData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (task) => {
    const confirmResult = await Swal.fire({
      title: translate("confirmDelete"),
      text: translate("deleteTaskWarning"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: translate("deleteButton"),
      cancelButtonText: translate("cancelButton"),
    });

    if (confirmResult.isConfirmed) {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const formData = new FormData();
        formData.append("id", task.id); // Append task ID

        const response = await fetch(
          `${process.env.REACT_APP_CHECK_USER_API}/api/admin/delete-announcement`,
          {
            method: "POST",
            body: formData, // Send formData instead of JSON
          }
        );

        const data = await response.json();

        if (data.success) {
          Swal.fire({
            title: translate("deleted"),
            text: translate("deleteSuccess"),
            icon: "success",
          });

          // Refresh the table data
          setPendingData((prev) => prev.filter((item) => item.id !== task.id));
        } else {
          Swal.fire({
            title: translate("error"),
            text: translate("deleteFailed"),
            icon: "error",
          });
        }
      } catch (error) {
        console.error("Delete Error:", error);
        Swal.fire({
          title: translate("error"),
          text: translate("deleteFailed"),
          icon: "error",
        });
      }
    }
  };
  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = pendingData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(pendingData.length / rowsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  const handleView = (task) => {
    setSelectedTask(task);
    setModalShow(true);
  };

  const handleApprove = (taskId) => {
    Swal.fire({
      title: translate('approveTitle'),
      text: translate('approveText'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: translate('approveConfirm'),
    }).then((result) => result.isConfirmed && approveTask(taskId));
  };

  const handleReject = (taskId) => {
    Swal.fire({
      title: translate('rejectTitle'),
      text: translate('rejectText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: translate('rejectConfirm'),
    }).then((result) => result.isConfirmed && rejectTask(taskId));
  };

  const approveTask = async (taskId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const formData = new FormData();
      formData.append('id', taskId);
      formData.append('user_id', user.id);
      formData.append('status', 'Approved');

      const response = await fetch(`${process.env.REACT_APP_CHECK_USER_API}/api/approval`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        Swal.fire(translate('approvedTitle'), translate('approvedText'), 'success');
        fetchPendingData();
      } else {
        Swal.fire(translate('errorTitle'), data.message, 'error');
      }
    } catch (error) {
      console.error(translate('approveError'), error);
      Swal.fire(translate('errorTitle'), translate('approveError'), 'error');
    }
  };

  const rejectTask = async (taskId) => {
    try {
      const { value: reason } = await Swal.fire({
        title: translate('provideRejectionReason'),
        input: 'textarea',
        inputLabel: translate('rejectionReason'),
        inputPlaceholder: translate('rejectionPlaceholder'),
        showCancelButton: true,
        confirmButtonText: translate('submit'),
        cancelButtonText: translate('cancel'),
      });

      if (!reason) {
        Swal.fire(translate('rejectionCancelled'), translate('rejectionCancelledText'), 'info');
        return;
      }

      const user = JSON.parse(localStorage.getItem('user'));
      const formData = new FormData();
      formData.append('id', taskId);
      formData.append('user_id', user.id);
      formData.append('status', 'Rejected');
      formData.append('reject_reason', reason);

      const response = await fetch(`${process.env.REACT_APP_CHECK_USER_API}/api/approval`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        Swal.fire(translate('rejectedTitle'), translate('rejectedText'), 'success');
        fetchPendingData();
      } else {
        Swal.fire(translate('errorTitle'), data.message, 'error');
      }
    } catch (error) {
      console.error(translate('rejectError'), error);
      Swal.fire(translate('errorTitle'), translate('rejectError'), 'error');
    }
  };

  return (
    <div className="bg-white shadow rounded-lg mb-8">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          {translate('pendingAnnouncements')}
        </h3>
      </div>
      <div className="border-t border-gray-200">
        {isLoading ? (
          <div className="px-4 py-6 text-center text-gray-500">
            {translate('loading')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('title')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('submittedBy')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRows.map((row, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {row.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(row.sent_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        {translate(row.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleView(row)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-custom hover:text-custom-dark border border-custom rounded-button"
                      >
                        {translate("viewButton")}
                      </button>
                      <button
                        onClick={() => handleApprove(row.id)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-button"
                      >
                        {translate("approveButton")}
                      </button>
                      <button
                        onClick={() => handleReject(row.id)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-button"
                      >
                        {translate("rejectButton")}
                      </button>
                      <button
                        onClick={() => handleDelete(row)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 border border-red-600 rounded-button"
                      >
                        {translate("deleteButton")}
                      </button>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="d-flex justify-between items-center px-6 py-3 space-x-2">
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg ${currentPage === 1
            ? "bg-gray-300 text-gray-500"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
        >
          {translate("prevButton")}
        </button>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg ${currentPage === totalPages
            ? "bg-gray-300 text-gray-500"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
        >
          {translate("nextButton")}
        </button>
      </div>

      <Modal size="xl" show={modalShow} onHide={() => setModalShow(false)} centered dialogClassName="modal-75w">
        <Modal.Header closeButton>
          <Modal.Title>{translate('taskDetails')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>{translate('field')}</th>
                <th>{translate('value')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{translate('title')}</td>
                <td>{selectedTask?.title || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('content')}</td>
                <td>{selectedTask?.content || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('submittedBy')}</td>
                <td>{selectedTask?.username || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('approvalStatus')}</td>
                <td>{selectedTask?.status || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('approvalDate')}</td>
                <td>
                  {selectedTask?.approved_date
                    ? new Date(selectedTask?.approved_date).toLocaleDateString()
                    : translate('notApproved')}
                </td>
              </tr>
              <tr>
                <td>{translate('rejectionReason')}</td>
                <td>{selectedTask?.rejection_reason || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('method')}</td>
                <td>{selectedTask?.method || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('createdAt')}</td>
                <td>{new Date(selectedTask?.created_at).toLocaleDateString() || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('sentAt')}</td>
                <td>{new Date(selectedTask?.sent_at).toLocaleDateString() || translate('notAvailable')}</td>
              </tr>
            </tbody>
          </table>
        </Modal.Body>
      </Modal>
    </div>

  );
};

export default PendingTable;
